import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc, deleteDoc, collection, addDoc, getDocs, query, where, orderBy, serverTimestamp, arrayUnion, arrayRemove, increment, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebaseConfig';
import { FaRegHeart, FaHeart, FaShare, FaMoon, FaEllipsisH, FaComment, FaExpand, FaTimes, FaTrash, FaMagic, FaEdit } from 'react-icons/fa';
import ShareModal from './ShareModal';
import { interpretDream } from '../utils/dreamInterpretation';
import ReactMarkdown from 'react-markdown';
import { timeAgo } from '../utils/timeAgo';
import Comment from './Comment';
import { EllipsisVerticalIcon, HeartIcon as HeartIconSolid, HeartIcon, ChatBubbleLeftIcon, ShareIcon, SparklesIcon } from '@heroicons/react/24/outline';
import ProfilePicture from './ProfilePicture';
import AdUnit from './AdSense';
import { checkUserRole } from '../utils/userManagement';
import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { ChevronDown, ChevronUp, BookOpen } from 'lucide-react';
import { DreamInterpretationResult } from '../utils/dreamInterpretationResult';
import {Link} from 'react-router-dom';
import { calculateFeatureCost } from '../utils/stripe';
import { FEATURE_PRICING } from '../utils/stripe';
import { generateDreamImage, saveGeneratedImageToStorage } from './PostForm';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles
import { useNotifications } from '../contexts/NotificationContext';

const ART_STYLES = [
    { name: 'surreal', label: 'Surreal' },
    { name: 'abstract', label: 'Abstract' },
    { name: 'realistic', label: 'Realistic' },
    { name: 'anime', label: 'Anime' },
    { name: 'digital_art', label: 'Digital Art' },
    { name: 'watercolor', label: 'Watercolor' },
    { name: 'oil_painting', label: 'Oil Painting' },
    { name: 'pencil_sketch', label: 'Pencil Sketch' },
    { name: 'pop_art', label: 'Pop Art' },
    { name: 'minimalist', label: 'Minimalist' }
];

const canInterpretPost = (post) => {
    // If no current user, cannot interpret
    if (!auth.currentUser) return false;
    
    // Cannot interpret own post
    if (post.userId === auth.currentUser.uid) return true;
    
    // Allow interpretation for all other users
    return false;
};

const generateInterpretation = async (content) => {
  try {
    const API_KEY = process.env.VITE_GEMINI_API_KEY || process.env.REACT_APP_GEMINI_API_KEY;
    
    if (!API_KEY) {
      throw new Error('No Gemini API Key found');
    }

    const genAI = new GoogleGenerativeAI(API_KEY);
    const model = genAI.getGenerativeModel({ model: "gemini-2.0-flash" });

    const prompt = `You are a dream interpreter. Provide a deep, insightful, and poetic interpretation of the following dream or description. Focus on symbolism, emotional undertones, and potential psychological insights. 

Dream/Description: "${content}"

Interpretation:`;

    const result = await model.generateContent(prompt);
    const response = await result.response;
    const interpretation = response.text();

    return interpretation;
  } catch (error) {
    console.error('Error generating dream interpretation:', error);
    throw error;
  }
};

const getArtStyleLabel = (styleName) => {
    const ART_STYLES = [
        { name: 'art-nouveau-generator', label: 'Art Nouveau' },
        { name: 'surreal-generator', label: 'Surrealism' },
        { name: 'cubist-generator', label: 'Cubism' },
        { name: 'impressionist-generator', label: 'Impressionism' },
        { name: 'anime-generator', label: 'Anime' },
        { name: 'fantasy-art-generator', label: 'Fantasy' },
        { name: 'pixel-art-generator', label: 'Pixel Art' },
        { name: 'comic-book-generator', label: 'Comic Book' },
        { name: 'watercolor-generator', label: 'Watercolor' },
        { name: 'steampunk-generator', label: 'Steampunk' },
        { name: 'art-deco-generator', label: 'Art Deco' },
        { name: 'gothic-generator', label: 'Gothic' },
        { name: 'cyberpunk-generator', label: 'Cyberpunk' },
        { name: 'dystopian-cyberpunk', label: 'Dystopian Cyberpunk' }
    ];

    if (!styleName) return 'Default';
    
    const style = ART_STYLES.find(s => s.name === styleName);
    return style ? style.label : 'Custom Style';
};

const Post = ({ 
    post, 
    onDelete = () => {}, 
    onEdit = () => {
        console.warn('Edit functionality not implemented');
        alert('Edit functionality is not available');
    }, 
    isOwnPost, 
    userProfile 
}) => {
    const [liked, setLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(post.likeCount || 0);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showInterpretation, setShowInterpretation] = useState(false);
    const [interpretation, setInterpretation] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState([]);
    const [newComment, setNewComment] = useState('');
    const [commentCount, setCommentCount] = useState(0);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [currentUserRole, setCurrentUserRole] = useState('simple');
    const [isEditingInterpretation, setIsEditingInterpretation] = useState(false);
    const [editedInterpretation, setEditedInterpretation] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editedContent, setEditedContent] = useState(post.content);
    const [editedTags, setEditedTags] = useState(post.tags || []);
    const [editedVisibility, setEditedVisibility] = useState(post.visibility || 'public');
    const [regenerateImage, setRegenerateImage] = useState(false);
    const [selectedArtStyle, setSelectedArtStyle] = useState(post.artStyle || '');
    const navigate = useNavigate();
    const isAuthenticated = auth.currentUser !== null;
    const canDelete = post.userId === auth.currentUser?.uid;
    const { currentUser, userRole } = useContext(AuthContext);
    const { createNotification } = useNotifications();

    const modules = {
        toolbar: [
          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          ['link', 'image'],
          ['clean']
        ]
      };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'list', 'bullet',
        'link', 'image'
    ];

    const renderContentWithHighlights = (content) => {
        // Highlight tags
        let renderedContent = content.replace(/#(\w+)/g, (match, tag) => 
          `<span class="bg-purple-100 text-purple-800 px-1 rounded">#${tag}</span>`
        );
    
        // Highlight mentions
        renderedContent = renderedContent.replace(/@(\w+)/g, (match, mention) => 
            `<a href="/dreamer/${mention}" class="bg-blue-100 text-blue-800 px-1 rounded hover:bg-blue-200 transition-colors">${match}</a>`
    );
    
        return renderedContent;
      };

    const handleSaveEdit = async () => {
        try {
            // Validate content
            if (!editedContent.trim()) {
                alert('Content cannot be empty');
                return;
            }

            // Check user credits for image regeneration
            const user = auth.currentUser;
            const userDoc = await getDoc(doc(db, 'profiles', user.uid));
            const userCredits = userDoc.data()?.credits || 0;

            // Update post in Firebase
            const postRef = doc(db, 'posts', post.id);
            let newImageUrl = post.imageUrl;

            // Regenerate image if checkbox is checked and user has credits
            if (regenerateImage && userCredits > 0) {
                try {
                    const selectedStyle = ART_STYLES.find(style => style.name === selectedArtStyle) || ART_STYLES[0];
                    newImageUrl = await generateDreamImage(editedContent, selectedStyle);
                    
                    // Deduct credit
                    const userProfileRef = doc(db, 'profiles', user.uid);
                    await updateDoc(userProfileRef, {
                        credits: increment(-1)
                    });
                } catch (imageError) {
                    console.error('Image generation error:', imageError);
                    alert('Failed to regenerate image. Using existing image.');
                }
            }

            // Update post document
            await updateDoc(postRef, {
                content: editedContent,
                tags: editedTags,
                visibility: editedVisibility,
                imageUrl: newImageUrl,
                artStyle: selectedArtStyle,
                updatedAt: serverTimestamp()
            });

            // Update local state and exit editing mode
            setIsEditing(false);
            
            // Optional: Trigger parent component's onEdit if needed
            if (typeof onEdit === 'function') {
                onEdit({
                    ...post,
                    content: editedContent,
                    tags: editedTags,
                    visibility: editedVisibility,
                    imageUrl: newImageUrl,
                    artStyle: selectedArtStyle
                });
            }

            // Create a notification for the post owner
            if (post.userId !== auth.currentUser.uid) {
                await createNotification(
                    post.userId, 
                    'edit', 
                    `${auth.currentUser.displayName} edited your post`, 
                    post.id
                );
            }

            alert('Post updated successfully!');
        } catch (error) {
            console.error('Error updating post:', error);
            alert('Failed to update post');
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedContent(post.content);
        setEditedTags(post.tags || []);
        setEditedVisibility(post.visibility || 'public');
        setRegenerateImage(false);
        setSelectedArtStyle(post.artStyle || '');
    };

    const handleTagInput = (e) => {
        const tagInput = e.target.value;
        const newTags = tagInput.split(',')
            .map(tag => tag.trim())
            .filter(tag => tag.length > 0);
        setEditedTags(newTags);
    };

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const profileRef = doc(db, 'profiles', post.userId);
                const profileSnap = await getDoc(profileRef);
                
                
                if (profileSnap.exists()) {
                    setProfileData(profileSnap.data());
                }
            } catch (error) {
                console.error('Error fetching profile:', error);
            }
            const fetchUserRole = async () => {
                try {
                    if (auth.currentUser) {
                        const userRole = await checkUserRole(auth.currentUser.uid);
                        setCurrentUserRole(userRole);
                    }
                } catch (error) {
                    console.error('Error fetching user role:', error);
                }    
            };
            fetchUserRole();
        };

        fetchProfileData();
    }, [post.userId]);

    useEffect(() => {
        if (isAuthenticated && auth.currentUser) {
            const checkIfLiked = async () => {
                try {
                    const postRef = doc(db, 'posts', post.id);
                    const postDoc = await getDoc(postRef);
                    if (postDoc.exists()) {
                        const postData = postDoc.data();
                        const likes = postData.likes || [];
                        setLiked(Array.isArray(likes) && likes.includes(auth.currentUser.uid));
                        setLikeCount(Array.isArray(likes) ? likes.length : 0);
                    }
                } catch (error) {
                    console.error('Error checking like status:', error);
                }
            };
            checkIfLiked();
        }
    }, [isAuthenticated, post.id]);

    useEffect(() => {
        if (showComments) {
            fetchComments();
        }
    }, [showComments, post.id]);

    useEffect(() => {
        // Check if user is a simple user (not premium)
        const checkUserStatus = async () => {
            if (!auth.currentUser) {
                setCurrentUserRole('simple');
                return;
            }
            try {
                const userDoc = await getDoc(doc(db, 'profiles', auth.currentUser.uid));
                setCurrentUserRole(userDoc.data()?.role || 'simple');
            } catch (error) {
                console.error('Error checking user status:', error);
                setCurrentUserRole('simple');
            }
        };
        checkUserStatus();
    }, []);

    useEffect(() => {
        const fetchPostDetails = async () => {
            try {
                // Fetch comments
                const commentsRef = collection(db, 'posts', post.id, 'comments');
                const commentsQuery = query(commentsRef, orderBy('createdAt', 'desc'));
                const commentsSnapshot = await getDocs(commentsQuery);
                const fetchedComments = commentsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setComments(fetchedComments);
                setCommentCount(fetchedComments.length);

                // Fetch interpretation
                const interpretationsRef = collection(db, 'posts', post.id, 'interpretations');
                const interpretationQuery = query(
                    interpretationsRef, 
                    where('userId', '==', post.userId)
                );
                const interpretationSnapshot = await getDocs(interpretationQuery);
                
                if (!interpretationSnapshot.empty) {
                    const interpretationDoc = interpretationSnapshot.docs[0];
                    setInterpretation({
                        id: interpretationDoc.id,
                        ...interpretationDoc.data()
                    });
                }
            } catch (error) {
                console.error('Error fetching post details:', error);
            }
        };

        fetchPostDetails();
    }, [post.id]);

    const fetchComments = async () => {
        try {
            setIsLoadingComments(true);
            const commentsRef = collection(db, 'posts', post.id, 'comments');
            const commentsQuery = query(commentsRef, orderBy('createdAt', 'desc'));
            const commentsSnap = await getDocs(commentsQuery);
            const commentsData = commentsSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setComments(commentsData);
            setCommentCount(commentsData.length);
        } catch (error) {
            console.error('Error fetching comments:', error);
        } finally {
            setIsLoadingComments(false);
        }
    };

    const handleLike = async () => {
        try {
            if (!auth.currentUser) {
                console.error('No current user for like action');
                return;
            }

            const postRef = doc(db, 'posts', post.id);
            const userProfileRef = doc(db, 'profiles', auth.currentUser.uid);
            
            if (liked) {
                // Unlike logic
                await updateDoc(postRef, {
                    likes: arrayRemove(auth.currentUser.uid)
                });
                await updateDoc(userProfileRef, {
                    caughtDreams: arrayRemove(post.id)
                });
                setLiked(false);
                setLikeCount(prev => prev - 1);
            } else {
                // Like logic
                await updateDoc(postRef, {
                    likes: arrayUnion(auth.currentUser.uid)
                });
                await updateDoc(userProfileRef, {
                    caughtDreams: arrayUnion(post.id)
                });
                setLiked(true);
                setLikeCount(prev => prev + 1);
            }

            // Debug logging for notification
            console.log('Post Owner ID:', post.userId);
            console.log('Current User ID:', auth.currentUser.uid);
            console.log('Current User Display Name:', auth.currentUser.displayName);

            // Create a notification for the post owner
            if (post.userId && post.userId !== auth.currentUser.uid) {
                try {
                    await createNotification(
                        post.userId, 
                        'like', 
                        `${auth.currentUser.displayName} liked your post`, 
                        post.id
                    );
                    console.log('Notification created successfully');
                } catch (error) {
                    console.error('Failed to create notification:', error);
                }
            } else {
                console.warn('Skipping notification: Same user or missing post owner ID');
            }
        } catch (error) {
            console.error('Error updating like:', error);
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this dream?')) {
            try {
                await deleteDoc(doc(db, 'posts', post.id));
                if (onDelete) onDelete(post.id);

                // Create a notification for the post owner
                if (post.userId !== auth.currentUser.uid) {
                    await createNotification(
                        post.userId, 
                        'delete', 
                        `${auth.currentUser.displayName} deleted your post`, 
                        post.id
                    );
                }
            } catch (error) {
                console.error('Error deleting post:', error);
            }
        }
    };

    const handleShare = async () => {
        setShowShareModal(true);
    }

    const handleInterpretDream = async () => {
        // Check if user can interpret the post
        if (!canInterpretPost(post)) {
            alert('You cannot interpret your own post.');
            return;
        }

        try {
            // Fetch dream interpretation cost based on user role
            const userRef = doc(db, 'profiles', auth.currentUser.uid);
            const userDoc = await getDoc(userRef);
            const userData = userDoc.data();
            const userRole = userData.role || 'simple';

            // Calculate interpretation cost
            const roleConfig = FEATURE_PRICING.roles[userRole];
            const baseInterpretationCost = FEATURE_PRICING.dreamInterpretation;
            const interpretationCost = baseInterpretationCost * 
                (1 - (roleConfig.interpretationDiscount || 0));

            // Check credit availability
            const hasFreeCredits = userData.monthlyFreeCredits > 0;
            const hasEnoughCredits = (userData.credits || 0) >= interpretationCost;

            if (!hasFreeCredits && !hasEnoughCredits) {
                alert('Insufficient credits to interpret this dream.');
                return;
            }

            // Deduct credits
            if (hasFreeCredits) {
                await updateDoc(userRef, {
                    monthlyFreeCredits: increment(-1)
                });
            } else {
                await updateDoc(userRef, {
                    credits: increment(-interpretationCost)
                });
            }

            // Interpret dream
            const interpretationResult = await generateInterpretation(post.content);

            // Save interpretation to Firestore
            const interpretationRef = collection(db, 'posts', post.id, 'interpretations');
            await addDoc(interpretationRef, {
                postId: post.id,
                content: interpretationResult,
                userId: auth.currentUser.uid,
                createdAt: new Date(),
                cost: interpretationCost
            });

            // Update local state
            setInterpretation({
                content: interpretationResult,
                userId: auth.currentUser.uid,
                createdAt: new Date(),
                cost: interpretationCost
            });

            setShowInterpretation(true);

            // Create a notification for the post owner
            if (post.userId !== auth.currentUser.uid) {
                await createNotification(
                    post.userId, 
                    'interpret', 
                    `${auth.currentUser.displayName} interpreted your post`, 
                    post.id
                );
            }
        } catch (error) {
            console.error('Dream Interpretation Error:', error);
            alert('Failed to post dream. Please try again.');   
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        if (!newComment.trim()) return;

        const user = auth.currentUser;
        if (!user) {
            navigate('/login');
            return;
        }

        try {
            const commentRef = collection(db, 'posts', post.id, 'comments');
            await addDoc(commentRef, {
                content: newComment,
                userId: user.uid,
                userDisplayName: user.displayName || 'Anonymous',
                userPhotoURL: user.photoURL,
                createdAt: serverTimestamp(),
                replyCount: 0
            });

            setNewComment('');
            fetchComments();

            // Create a notification for the post owner
            if (post.userId !== auth.currentUser.uid) {
                await createNotification(
                    post.userId, 
                    'comment', 
                    `${auth.currentUser.displayName} commented on your post`, 
                    post.id
                );
            }
        } catch (error) {
            console.error('Error adding comment:', error);
        }
    };

    const handleReplySubmit = async (commentId, replyText) => {
        if (!auth.currentUser) {
            navigate('/login');
            return;
        }

        try {
            const replyRef = collection(db, 'posts', post.id, 'comments', commentId, 'replies');
            await addDoc(replyRef, {
                text: replyText,
                userId: auth.currentUser.uid,
                userDisplayName: auth.currentUser.displayName || 'Anonymous',
                userPhotoURL: auth.currentUser.photoURL,
                createdAt: serverTimestamp()
            });

            // Update the comment with the new reply count
            const commentRef = doc(db, 'posts', post.id, 'comments', commentId);
            await updateDoc(commentRef, {
                replyCount: increment(1)
            });

            // Refresh comments
            fetchComments();

            // Create a notification for the post owner
            if (post.userId !== auth.currentUser.uid) {
                await createNotification(
                    post.userId, 
                    'reply', 
                    `${auth.currentUser.displayName} replied to a comment on your post`, 
                    post.id
                );
            }
        } catch (error) {
            console.error('Error adding reply:', error);
        }
    };

    const handleDeleteComment = async (commentId) => {
        try {
            await deleteDoc(doc(db, 'posts', post.id, 'comments', commentId));
            fetchComments();

            // Create a notification for the post owner
            if (post.userId !== auth.currentUser.uid) {
                await createNotification(
                    post.userId, 
                    'delete_comment', 
                    `${auth.currentUser.displayName} deleted a comment on your post`, 
                    post.id
                );
            }
        } catch (error) {
            console.error('Error deleting comment:', error);
        }
    };

    const handleGenerateClick = () => {
        if (currentUserRole === 'simple') {
            setShowGenerateModal(true);
        } else {
            // Use interpretDream instead of handleGenerate
            handleInterpretDream();
        }
    };

    const handleEditInterpretation = () => {
        setEditedInterpretation(interpretation.content);
        setIsEditingInterpretation(true);
    };

    const handleUpdateInterpretation = async () => {
        const user = auth.currentUser;
        if (!user) {
            alert('Please log in to generate an interpretation');
            return;
        }

        const userRef = doc(db, 'profiles', user.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();

        // Check if user has enough credits
        if (userData.credits < 1) {
            alert('Insufficient credits. Please purchase more to continue.');
            return;
        }

        try {
            // Generate interpretation
            const interpretationResult = await generateInterpretation(post.content);

            // Save interpretation to Firestore
            const interpretationRef = collection(db, 'posts', post.id, 'interpretations');
            await addDoc(interpretationRef, {
                text: interpretationResult,
                userId: user.uid,
                createdAt: serverTimestamp()
            });

            // Deduct credit for interpretation
            await updateDoc(userRef, {
                credits: increment(-1)
            });

            // Update local state or trigger a refresh
            setInterpretation(interpretationResult);
        } catch (error) {
            console.error('Error generating interpretation:', error);
            alert('Failed to generate interpretation');
        }
    };

    const handleDeleteInterpretation = async () => {
        if (!window.confirm('Are you sure you want to delete this interpretation?')) return;

        try {
            const interpretationRef = doc(
                db, 
                'posts', 
                post.id, 
                'interpretations', 
                interpretation.id
            );

            await deleteDoc(interpretationRef);

            // Clear local state
            setInterpretation(null);

            // Create a notification for the post owner
            if (post.userId !== auth.currentUser.uid) {
                await createNotification(
                    post.userId, 
                    'delete_interpretation', 
                    `${auth.currentUser.displayName} deleted an interpretation on your post`, 
                    post.id
                );
            }
        } catch (error) {
            console.error('Error deleting interpretation:', error);
        }
    };

    const handleRegenerateImage = async () => {
        try {
            // Fetch user data
            const userRef = doc(db, 'profiles', auth.currentUser.uid);
            const userDoc = await getDoc(userRef);
            const userData = userDoc.data();
            const userRole = userData.role || 'simple';

            // Calculate image regeneration cost
            const roleConfig = FEATURE_PRICING.roles[userRole];
            const baseImageCost = FEATURE_PRICING.imageGeneration;
            const imageCost = baseImageCost * (1 - (roleConfig.imageGenerationDiscount || 0));

            // Check credit availability
            const hasFreeCredits = userData.monthlyFreeCredits > 0;
            const hasEnoughCredits = (userData.credits || 0) >= imageCost;

            if (!hasFreeCredits && !hasEnoughCredits) {
                alert('Insufficient credits to regenerate image.');
                return;
            }

            // Deduct credits
            if (hasFreeCredits) {
                await updateDoc(userRef, {
                    monthlyFreeCredits: increment(-1)
                });
            } else {
                await updateDoc(userRef, {
                    credits: increment(-imageCost)
                });
            }

            // Regenerate image using the original content and art style
            const newImageUrl = await generateDreamImage(
                post.content, 
                ART_STYLES.find(style => style.name === post.artStyle)
            );

            // Update post with new image
            await updateDoc(doc(db, 'posts', post.id), {
                imageUrl: newImageUrl
            });

            // Optionally update local state or trigger refresh
            alert('Image regenerated successfully!');

            // Create a notification for the post owner
            if (post.userId !== auth.currentUser.uid) {
                await createNotification(
                    post.userId, 
                    'regenerate_image', 
                    `${auth.currentUser.displayName} regenerated an image on your post`, 
                    post.id
                );
            }
        } catch (error) {
            console.error('Error regenerating image:', error);
            alert('Failed to regenerate image');
        }
    };

    const handleEdit = () => {
        if (typeof onEdit === 'function' && auth.currentUser?.uid === post.userId) {
            onEdit(post);
        } else {
            console.warn('Edit not allowed');
            alert('You can only edit your own posts');
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const optionsMenu = document.getElementById(`post-options-${post.id}`);
            if (optionsMenu && !optionsMenu.contains(event.target)) {
                setShowMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [post.id]);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <div className="p-4">
                <div className="px-4 pt-4 sm:px-6">
                    {/* Header with avatar, username, timestamp, and menu */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                            {currentUser && (
                                <Link to={`/dreamer/${post.username}`}>
                                <ProfilePicture
                                    imageUrl={profileData?.profilePic || profileData?.photoURL} 
                                    size="md" 
                                    alt={`${post.authorName}'s profile`}
                                    className="h-12 w-12 rounded-full"
                                />
                                </Link>
                                )} 
                
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {profileData?.username || post.username || 'Anonymous'}
                                </span>
                                <div className="flex items-center">
                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                        {timeAgo(post.createdAt)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Menu button */}
                        <div className="relative" id={`post-options-${post.id}`}>
                            <button
                                onClick={() => setShowMenu(!showMenu)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none z-40"
                            >
                                <EllipsisVerticalIcon className="h-5 w-5" />
                            </button>

                            {/* Dropdown menu */}
                            {showMenu && (
                                <div className="absolute right-0 top-full mt-1 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
                                    <div className="py-1">
                                        {auth.currentUser?.uid === post.userId && (
                                            <>
                                                <button
                                                    onClick={() => setIsEditing(true)}
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                >
                                                    Edit Dream
                                                </button>
                                                <button
                                                    onClick={handleDelete}
                                                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                                >
                                                    Delete Dream
                                                </button>
                                            </>
                                        )}
                                        
                                        {/* Regenerate image option for posts with generated images */}
                                        {post.canRegenerate && post.imageUrl && auth.currentUser?.uid === post.userId && (
                                            <button
                                                onClick={handleRegenerateImage}
                                                className="w-full text-left px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                                            >
                                                Regenerate Image
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Dream content */}
                    {isEditing ? (
                        <div className="mt-4 space-y-4">
                            {/* Visibility Select */}
                            <div className="flex items-center space-x-2">
                                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Visibility:
                                </label>
                                <div className="relative">
                                    <select
                                        value={editedVisibility}
                                        onChange={(e) => setEditedVisibility(e.target.value)}
                                        className="appearance-none w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md pl-3 pr-10 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                    >
                                        <option value="public">Public</option>
                                        <option value="private">Private</option>
                                        <option value="followers">Followers Only</option>
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
                                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* Quill Editor */}
                            <ReactQuill 
                                value={editedContent}
                                onChange={setEditedContent}
                                modules={modules}
                                formats={formats}
                                className="mb-4"
                            />
                            
                            {/* Regenerate Image Checkbox */}
                            <div className="flex items-center mt-2">
                                <input 
                                    type="checkbox"
                                    id="regenerateImage"
                                    checked={regenerateImage}
                                    onChange={() => setRegenerateImage(!regenerateImage)}
                                    className="mr-2"
                                />
                                <label htmlFor="regenerateImage" className="text-sm">
                                    Regenerate Image
                                </label>
                            </div>

                            {/* Art Style Dropdown (only show if regenerate is checked) */}
                            {regenerateImage && (
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Select New Style
                                    </label>
                                    <select
                                        value={selectedArtStyle}
                                        onChange={(e) => setSelectedArtStyle(e.target.value)}
                                        className="w-full p-2 border rounded-md dark:bg-gray-700 dark:text-white"
                                    >
                                        <option value="">Select a style</option>
                                        {ART_STYLES.map((style) => (
                                            <option 
                                                key={style.name} 
                                                value={style.name}
                                                disabled={style.isPro && userRole !== 'pro'}
                                            >
                                                {style.label} {style.isPro ? '(Pro)' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {selectedArtStyle && ART_STYLES.find(s => s.name === selectedArtStyle)?.isPro && userRole !== 'pro' && (
                                        <p className="mt-2 text-sm text-yellow-500">
                                            This style is only available to Pro users. Please upgrade to access it.
                                        </p>
                                    )}
                                </div>
                            )}
                            
                            {/* Tags Input */}
                            <input 
                                type="text"
                                value={editedTags.join(', ')}
                                onChange={handleTagInput}
                                placeholder="Enter tags separated by comma"
                                className="w-full p-2 mt-2 border rounded-md dark:bg-gray-700 dark:text-white"
                            />
                            
                            {/* Save and Cancel Buttons */}
                            <div className="flex justify-end mt-2 space-x-2">
                                <button 
                                    onClick={handleSaveEdit}
                                    className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
                                >
                                    Save
                                </button>
                                <button 
                                    onClick={handleCancelEdit}
                                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="text-gray-900 dark:text-white whitespace-pre-wrap mt-3 mb-4"
                                dangerouslySetInnerHTML={{ 
                                __html: renderContentWithHighlights(post.content) 
                                }} 
                            />
                            
                            {post.imageUrl && (
                                <div className="relative group">
                                    <img
                                        src={post.imageUrl}
                                        alt="Dream visualization"
                                        className="w-full aspect-w-16 aspect-h-9 rounded-lg cursor-pointer hover:opacity-95 transition-opacity"
                                        onClick={() => setShowImageModal(true)}
                                    />
                                    <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                                        {getArtStyleLabel(post.artStyle)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* Optional: Display tags and mentions separately */}
                    {/* {post.tags && post.tags.length > 0 && (
                        <div className="mt-2">
                          {post.tags.map(tag => (
                            <span 
                              key={tag} 
                              className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs mr-2"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {post.mentions && post.mentions.length > 0 && (
                        <div className="mt-2">
                          {post.mentions.map(mention => (
                            <span 
                              key={mention} 
                              className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs mr-2"
                            >
                              @{mention}
                            </span>
                          ))}
                        </div>
                      )} */}
                    {/* Interaction buttons */}
                    <div className="flex justify-between items-center py-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={handleLike}
                                className={`flex items-center space-x-1.5 ${
                                    liked ? 'text-pink-500' : 'text-gray-500 dark:text-gray-400'
                                }`}
                            >
                                {liked ? (
                                    <HeartIconSolid className="h-5 w-5" />
                                ) : (
                                    <HeartIcon className="h-5 w-5" />
                                )}
                                <span>{likeCount}</span>
                            </button>

                            <button
                                onClick={() => setShowComments(!showComments)}
                                className="flex items-center space-x-1.5 text-gray-500 dark:text-gray-400"
                            >
                                <ChatBubbleLeftIcon className="h-5 w-5" />
                                <span>{commentCount}</span>
                            </button>

                            {canInterpretPost(post) && (
                                <button
                                    onClick={handleInterpretDream}
                                    className="flex items-center space-x-1.5 text-gray-500 dark:text-gray-400"
                                    disabled={isLoading}
                                >
                                    <SparklesIcon className="h-5 w-5" />
                                    <span>{isLoading ? 'Interpreting...' : 'Interpret'}</span>
                                </button>
                            )}

                            <button
                                onClick={handleShare}
                                className="flex items-center space-x-1.5 text-gray-500 dark:text-gray-400"
                            >
                                <ShareIcon className="h-5 w-5" />
                                <span>Share</span>
                            </button>
                        </div>
                    </div>

                    {/* Comments section */}
                    {showComments && (
                        <div className="border-t border-gray-200 dark:border-gray-700">
                            <div className="p-4">
                                {/* Comment form */}
                                <form onSubmit={handleAddComment} className="mb-4">
                                    <div className="flex space-x-2">
                                        <textarea
                                            value={newComment}
                                            onChange={(e) => setNewComment(e.target.value)}
                                            placeholder="Write a comment..."
                                            className="flex-grow px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
                                            rows="2"
                                            maxLength={1000}
                                        />
                                        <button
                                            type="submit"
                                            disabled={!newComment.trim()}
                                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                        >
                                            Post
                                        </button>
                                    </div>
                                    <div className="mt-1 text-right">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {1000 - newComment.length} characters remaining
                                        </span>
                                    </div>
                                </form>

                                {/* Comments list */}
                                {isLoadingComments ? (
                                    <div className="flex items-center justify-center space-x-2 py-4">
                                        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                        <span className="text-sm text-gray-500 dark:text-gray-400">Loading comments...</span>
                                    </div>
                                ) : (
                                    <div className="mt-4 space-y-4">
                                        {comments.map((comment) => (
                                            <Comment
                                                key={comment.id}
                                                comment={comment}
                                                onReply={handleReplySubmit}
                                                currentUser={auth.currentUser}
                                                postId={post.id}
                                            />
                                        ))}
                                        {comments.length === 0 && (
                                            <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                                No comments yet. Be the first to comment!
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Dream Interpretation Modal */}
                {interpretation && (
                    <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg mt-2 relative">
                        
                        {isEditingInterpretation ? (
                            <div>
                                <textarea
                                    value={editedInterpretation}
                                    onChange={(e) => setEditedInterpretation(e.target.value)}
                                    className="w-full p-2 border rounded-md dark:bg-gray-700 dark:text-white"
                                    rows={4}
                                />
                                <div className="flex justify-end space-x-2">
                                    <button 
                                        onClick={() => setIsEditingInterpretation(false)}
                                        className="text-gray-500 mr-2"
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        onClick={handleUpdateInterpretation}
                                        className="bg-purple-600 text-white px-4 py-2 rounded"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-800 dark:text-gray-200">
                                {interpretation && (
                            <DreamInterpretationResult interpretation={interpretation.content} />
                        )}
                                    {/* {interpretation.content} */}
                                </p>
                                {isOwnPost && (
                                    <div className="absolute top-2 right-2 flex space-x-2">
                                        <button 
                                            onClick={handleEditInterpretation}
                                            className="text-blue-500 hover:text-blue-700"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button 
                                            onClick={handleDeleteInterpretation}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Image Modal */}
                {showImageModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={() => setShowImageModal(false)}>
                        <div className="relative max-w-[90vw] max-h-[90vh]">
                            <img
                                src={post.imageUrl}
                                alt="Dream visualization"
                                className="max-w-full max-h-[90vh] object-contain"
                                onClick={(e) => e.stopPropagation()}
                            />
                            <button
                                onClick={() => setShowImageModal(false)}
                                className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                            >
                                <FaTimes className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}

                {/* Share modal */}
                {showShareModal && (
                    <ShareModal
                        isOpen={showShareModal}
                        onClose={() => setShowShareModal(false)}
                        post={{
                            ...post,
                            authorName: post.username || 'Anonymous',
                            authorPhotoURL: post.userPhotoURL,
                        }}
                    />
                )}

                {/* Generate Modal for Simple Users */}
                {showGenerateModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-lg w-full mx-4">
                            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
                                Generate Dream Image
                            </h3>
                            <div className="mb-6">
                                <p className="text-gray-600 dark:text-gray-300 mb-4">
                                    To generate dream images, you can either:
                                </p>
                                <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                                    <li>Upgrade to Premium for unlimited generations</li>
                                    <li>Watch a short ad to generate one image</li>
                                </ul>
                            </div>
                            
                            <div className="mb-6">
                                <AdUnit adSlot="6045190321" /> {/* Replace with your actual ad slot */}
                            </div>

                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setShowGenerateModal(false)}
                                    className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        setShowGenerateModal(false);
                                        handleInterpretDream();
                                    }}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    Generate After Ad
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Post;